/**
 * useSpeechRecognition Hook
 *
 * A React hook for speech-to-text functionality using the Web Speech API.
 * Provides voice input capabilities with real-time transcription.
 *
 * Features:
 * - Real-time speech transcription
 * - Interim results for live feedback
 * - Configurable language support
 * - Automatic browser compatibility detection
 * - Error handling and recovery
 *
 * @example
 * const {
 *   transcript,
 *   interimTranscript,
 *   isListening,
 *   startListening,
 *   stopListening,
 *   resetTranscript,
 *   browserSupported,
 *   error
 * } = useSpeechRecognition({ language: 'en-US', continuous: false });
 */

import { useState, useEffect, useCallback, useRef } from 'react';

// Check for browser support
const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const browserSupported = !!SpeechRecognition;

/**
 * Custom hook for speech-to-text functionality
 * @param {Object} options - Configuration options
 * @param {string} options.language - Language code (default: 'en-US')
 * @param {boolean} options.continuous - Keep listening after speech ends (default: false)
 * @param {boolean} options.interimResults - Show results while speaking (default: true)
 * @param {function} options.onResult - Callback when final result is available
 * @param {function} options.onError - Callback when error occurs
 * @returns {Object} Speech recognition state and controls
 */
const useSpeechRecognition = (options = {}) => {
  const {
    language = 'en-US',
    continuous = false,
    interimResults = true,
    onResult,
    onError,
  } = options;

  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState(null);

  const recognitionRef = useRef(null);
  const finalTranscriptRef = useRef('');

  // Initialize recognition instance
  useEffect(() => {
    if (!browserSupported) {
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = continuous;
    recognition.interimResults = interimResults;
    recognition.lang = language;

    recognition.onstart = () => {
      setIsListening(true);
      setError(null);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.onresult = (event) => {
      let interimText = '';
      let finalText = finalTranscriptRef.current;

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const text = result[0].transcript;

        if (result.isFinal) {
          finalText += text + ' ';
          finalTranscriptRef.current = finalText;
          setTranscript(finalText.trim());

          if (onResult) {
            onResult(text.trim(), finalText.trim());
          }
        } else {
          interimText += text;
        }
      }

      setInterimTranscript(interimText);
    };

    recognition.onerror = (event) => {
      let errorMessage = 'Speech recognition error';

      switch (event.error) {
        case 'no-speech':
          errorMessage = 'No speech detected. Please try again.';
          break;
        case 'audio-capture':
          errorMessage = 'No microphone found. Please check your microphone.';
          break;
        case 'not-allowed':
          errorMessage = 'Microphone access denied. Please allow microphone access.';
          break;
        case 'network':
          errorMessage = 'Network error. Please check your internet connection.';
          break;
        case 'aborted':
          errorMessage = 'Speech recognition was aborted.';
          break;
        default:
          errorMessage = `Error: ${event.error}`;
      }

      setError(errorMessage);
      setIsListening(false);

      if (onError) {
        onError(event.error, errorMessage);
      }
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, [language, continuous, interimResults, onResult, onError]);

  // Start listening
  const startListening = useCallback(() => {
    if (!browserSupported) {
      setError('Speech recognition is not supported in this browser.');
      return;
    }

    if (recognitionRef.current && !isListening) {
      setError(null);
      try {
        recognitionRef.current.start();
      } catch (err) {
        // Recognition might already be started
        if (err.name !== 'InvalidStateError') {
          setError('Failed to start speech recognition.');
        }
      }
    }
  }, [isListening]);

  // Stop listening
  const stopListening = useCallback(() => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
    }
  }, [isListening]);

  // Reset transcript
  const resetTranscript = useCallback(() => {
    setTranscript('');
    setInterimTranscript('');
    finalTranscriptRef.current = '';
  }, []);

  // Toggle listening state
  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  return {
    // State
    transcript,
    interimTranscript,
    isListening,
    error,
    browserSupported,

    // Controls
    startListening,
    stopListening,
    toggleListening,
    resetTranscript,
  };
};

export default useSpeechRecognition;
export { browserSupported as speechRecognitionSupported };
